import { useRemoteSearchParams, REMOTE_SEARCH_STATE, useNavigateWithSearchParams } from 'hooks';
import { appStore, uiStore } from 'store';
import { loadSurveySteps } from '@verv-inc/js-questionnaire';
import { Router, ROUTES, isPlanRoute } from 'router';
import React, { FC, useEffect } from 'react';
import { useT } from 'talkr';
import { Loader } from 'UI';
import { composeRemoteConfigProvider, remoteConfigIDSearchKey } from 'utils/remoteConfig';
import { ToastContainer } from 'react-toastify';
import PaymentErrorModal from 'modules/payment/components/paymentErrorModal';
import { observer } from 'mobx-react-lite';
import firebaseConfig from 'firebaseConfig';
import { THEME_TYPE } from 'interfaces';
import { SURVEY_CONFIG } from '@types';
import imageDownloader from './utils/imageDownloader';
import checkDeviceTheme from './utils/custom/checkDeviceTheme';
import { Alert } from 'UI/alert';
import { NewGTMOnbordingQuestionEvent } from 'utils/analytics/events/GTMOnbordingQuestionEvents';
import { EVENT_TRACKERS } from 'utils/analytics';
// import getRandomValue from './utils/getRandomValue';

firebaseConfig();

const ViewportSizes: FC = () => {
	function setupViewportHandler() {
		const viewport = window.visualViewport;
		const layoutViewport = document.querySelector('.layoutViewport');

		// https://developer.mozilla.org/en-US/docs/Web/API/VisualViewport
		function viewportHandler() {
			// if (!layoutViewport) return;
			// Since the bar is position: fixed we need to offset it by the visual
			// viewport's offset from the layout viewport origin.
			const { offsetLeft } = viewport ?? {};
			const offsetTop =
				(viewport?.height ?? 0) -
				(layoutViewport?.getBoundingClientRect().height ?? 0) +
				(viewport?.offsetTop ?? 0);

			// You could also do this by setting style.left and style.top if you
			// use width: 100% instead.
			document.documentElement.style.setProperty('--offsetTop', offsetTop?.toString() ?? null);
			document.documentElement.style.setProperty('--offsetLeft', offsetLeft?.toString() ?? null);
			document.documentElement.style.setProperty(
				'--viewPortScale',
				viewport?.scale?.toString() ?? null,
			);
			document.documentElement.style.setProperty(
				'--viewportOffsetTop',
				viewport?.offsetTop?.toString() ?? null,
			);
		}

		window.visualViewport?.addEventListener('scroll', viewportHandler);
		window.visualViewport?.addEventListener('resize', viewportHandler);
	}

	useEffect(() => {
		setupViewportHandler();
	}, []);

	return <div className='layoutViewport' />;
};

export const App: FC = observer(() => {
	const navigate = useNavigateWithSearchParams();
	const { paymentErrorShow } = appStore.paymentNavigationStore;
	const { setLocale } = useT();
	const { params, setSourceSearchParams, sourceLocation, errorLogger, eventsTracker } = appStore;
	const [searchParams] = useRemoteSearchParams({
		configSearchKey: remoteConfigIDSearchKey,
		provider: composeRemoteConfigProvider(),
		errorLogger,
		defaultUrl: sourceLocation.href,
	});

	const { setSteps } = appStore.surveyStore;
	const { startPage, config } = params;
	const { setTheme } = uiStore;
	const gender = appStore.params.gender;

	const setShowPaymentError = (shown: boolean) => {
		appStore.paymentNavigationStore.setPaymentErrorShown(shown);
	};

	useEffect(() => {
		searchParams.value != null && setSourceSearchParams(new URLSearchParams(searchParams.value));
	}, [searchParams.value]);

	useEffect(() => {
		appStore.params.theme && setTheme(checkDeviceTheme());
	}, [appStore.params.theme, searchParams.value]);

	// useEffect(() => {
	// 	const {fsv1m1, fsv1m2, fsv1m3 } = appStore.paymentStore.settings;

	// 	if (fsv1m1 || fsv1m2 || fsv1m3) {
	// 		appStore.params.gender && setGender('male');
	// 		// console.log(appStore.params.gender);
	// 	} else {
	// 		// appStore.params.gender && setTheme(appStore.params.theme);
	// 	}
	// 	// console.log(appStore.params.gender);
	// }, [appStore.params.gender]);

	useEffect(() => {
		if (appStore.params.config === SURVEY_CONFIG.PILATES) {
			setLocale('en');
		} else {
			appStore.params.force_lang && setLocale(appStore.params.force_lang);
		}
	}, [appStore.params.force_lang, searchParams.value]);

	// FIXME: move config loading into appStore
	useEffect(() => {
		const gender = appStore.params.gender;
		if (appStore.params.config === SURVEY_CONFIG.PILATES) {
			const newEvent = NewGTMOnbordingQuestionEvent.answerQuestion({
				question: 'gender',
				answer: gender,
			});
			eventsTracker.trackWithTarget(newEvent, EVENT_TRACKERS.GTM).catch(errorLogger.logError);
		}

		if (
			isPlanRoute(window.location.pathname) &&
			searchParams.state !== REMOTE_SEARCH_STATE.IDLE &&
			searchParams.state !== REMOTE_SEARCH_STATE.LOADING
		) {
			const publicURL = process.env.PUBLIC_URL ?? '/';

			const configNameToLoad: string = searchParams.value?.config ?? config;
			// console.log('config: ', configNameToLoad);
			configNameToLoad &&
				loadSurveySteps(
					`${window.location.origin}${publicURL}/config/survey/${configNameToLoad}.json`,
					`${window.location.origin}${publicURL}/config/stepsConfig.json`,
				)
					.then(data => {
						if (data == null) errorLogger.logError(new Error('no steps loaded'));

						setSteps(data ?? []);
						const firstStep = data?.find(step => step.isFirstStep);
						if (startPage) {
							navigate(ROUTES.HOME);
						} else {
							firstStep?.id && navigate(`${ROUTES.SURVEY}/${firstStep.id}`);
						}
					})
					.catch(errorLogger.logError);

			setTimeout(() => {
				imageDownloader('first', gender);
			}, 600);
			// getRandomValue(100);
		}
	}, [searchParams.value]);

	return (
		<>
			<ViewportSizes />
			<ToastContainer />
			<PaymentErrorModal active={paymentErrorShow} setActive={setShowPaymentError} />
			{searchParams.state === REMOTE_SEARCH_STATE.IDLE ||
			searchParams.state === REMOTE_SEARCH_STATE.LOADING ? (
				<Loader />
			) : (
				<Router />
			)}
			<Alert />
			<div
				id='imageDownloader'
				style={{
					display: 'none',
					opacity: 0,
					zIndex: -9999,
					position: 'absolute',
				}}
			></div>
		</>
	);
});
